import { useRef, useState } from 'react';
import { Conversation } from '@elevenlabs/client';
import toast from 'react-hot-toast';

import type {
  ConversationInterface,
  ConversationParams,
  TranscriptEntry,
} from './useConversation';
import {
  ELEVEN_LABS_AGENT_ID,
  LANGUAGE_TO_VOICE_ID,
  SessionPhase,
  SUPPORTED_LANGUAGES,
} from '../utils/constants';
import { useSessionPhase } from './useSessionPhase';

export const use11Labs = ({
  session,
  startTimeRef,
  setIsAISpeaking,
  isIntroCompletedRef,
  setError,
  language,
  voiceId,
}: ConversationParams): ConversationInterface => {
  const conversationRef = useRef<Conversation | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const { setPhase } = useSessionPhase();

  const connectConversation = async (): Promise<void> => {
    console.log('11Labs connecting...');
    console.log('11Labs prompt:', session.voice.prompt.main);
    console.log('Using language:', language);
    console.log('Provided voiceId:', voiceId);
    setError(null);

    try {
      // Determine which language to use (priority: passed language > session language > default to English)
      const selectedLanguage = language || session?.language || 'en';
      console.log('Selected language after fallback:', selectedLanguage);

      const overrides: any = {
        agent: {
          prompt: { prompt: session.voice.prompt.main },
          firstMessage: session.voice.firstMessage.main,
          language: selectedLanguage, // Use the selected language for agent responses
        },
      };

      // First check if an explicit voiceId was provided
      if (voiceId) {
        console.log('Using explicitly provided voiceId:', voiceId);
        overrides.tts = { voiceId };
      }
      // Next check SUPPORTED_LANGUAGES for voiceId
      else {
        const langInfo = SUPPORTED_LANGUAGES.find(
          (lang) => lang.code === selectedLanguage,
        );
        if (langInfo?.voiceId) {
          console.log(
            `Found voice ID in SUPPORTED_LANGUAGES: ${langInfo.voiceId} for language ${selectedLanguage}`,
          );
          overrides.tts = {
            voiceId: langInfo.voiceId,
            // Use TTS locale if available
            locale: langInfo.TTS,
          };
        }
        // Fall back to LANGUAGE_TO_VOICE_ID if not in SUPPORTED_LANGUAGES
        else if (LANGUAGE_TO_VOICE_ID[selectedLanguage]) {
          const mappedVoiceId = LANGUAGE_TO_VOICE_ID[selectedLanguage];
          console.log(
            `Found voice mapping in LANGUAGE_TO_VOICE_ID: ${mappedVoiceId} for language ${selectedLanguage}`,
          );
          overrides.tts = { voiceId: mappedVoiceId };
        } else {
          console.log(
            `No voice mapping found for language: ${selectedLanguage}`,
          );
        }
      }

      const conversation = await Conversation.startSession({
        agentId: ELEVEN_LABS_AGENT_ID,
        connectionType: "webrtc",
        overrides,
        onConnect: () => {
          startTimeRef.current = Date.now();
          setPhase(SessionPhase.MAIN_CONVERSATION);
        },
        onDisconnect: () => setIsAISpeaking(false),
        onError: (error: string) => {
          const errorMessage = error || 'Connection Error';
          console.error('11Labs error:', errorMessage);
          setError(errorMessage);
          toast.error(errorMessage);
        },
        onModeChange: ({ mode }: { mode: string }) => {
          setIsAISpeaking(mode === 'speaking');
          console.log('11Labs mode changed:', mode);
        },
        onMessage: ({
          source,
          message,
        }: {
          source: 'ai' | 'user';
          message: string;
        }) => {
          if (!isIntroCompletedRef.current) return;
          setTranscript((prev) => [
            ...prev,
            { source, message, timestamp: new Date() },
          ]);
        },
      });

      conversationRef.current = conversation;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to connect to AI Coach';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const disconnectConversation = (): void => {
    if (conversationRef.current) {
      conversationRef.current.endSession();
      conversationRef.current = null;
    }
    setIsAISpeaking(false);
  };

  return {
    connectConversation,
    disconnectConversation,
    transcript,
  };
};
